import {
  Button,
  CheckboxMultiSelect,
  Input,
  NumberString,
  Select,
} from "@/components";
import { useAuth } from "@/contexts/AuthContext";
import { RoleEnum, RoleManagerExcute } from "@/types";
import { ROLE_LABELS } from "@/utils";
import { useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { useUpdateInforForm } from "../hooks";
import { UpdatePasswordForm } from "./UpdatePasswordForm";
import { isEmpty } from "lodash";

const UpdateInforForm = () => {
  const {
    control,
    register,
    roleOptionsFilter,
    errors,
    onSubmit,
    shopsInCompany,
    groupsInCompany,
    isDisabledStoreSelect,
    watch,
    isLoading,
    isMyProfile,
    hasUpdatePermission,
    handleResendInfor,
    handleSubmit,
  } = useUpdateInforForm();
  const { role } = useAuth();

  const [openPasswordModal, setOpenPasswordModal] = useState(false);

  const handleOpenPasswordModal = () => {
    setOpenPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setOpenPasswordModal(false);
  };

  const optionsForRole = useMemo(() => {
    switch (role) {
      case RoleEnum.COMPANY_ADMIN:
        return roleOptionsFilter;
      case RoleEnum.HEAD_OFFICE_USER:
        return roleOptionsFilter
          .filter((option) =>
            [
              RoleEnum.HEAD_OFFICE_USER,
              RoleEnum.STORE_MANAGER,
              RoleEnum.USER,
            ].includes(option.value)
          )
          .map((option) => ({
            ...option,
            disabled: option.value === RoleEnum.HEAD_OFFICE_USER,
          }));
      case RoleEnum.STORE_MANAGER:
        return roleOptionsFilter
          .filter((option) =>
            [RoleEnum.STORE_MANAGER, RoleEnum.USER].includes(option.value)
          )
          .map((option) => ({
            ...option,
            disabled: option.value === RoleEnum.STORE_MANAGER,
          }));
      case RoleEnum.USER:
        return roleOptionsFilter.filter(
          (option) => option.value === RoleEnum.USER
        );
      case RoleEnum.OTHER:
        return [{ label: ROLE_LABELS.other, value: RoleEnum.OTHER }];
      default:
        return roleOptionsFilter;
    }
  }, [role]);

  const currentUserRole = watch("role");

  return (
    <div
      className="flex flex-col gap-4 p-4 rounded-2xl bg-white relative"
      style={{
        boxShadow:
          "0 0 2px 0 rgba(145, 158, 171, 0.20), 0 12px 24px -4px rgba(145, 158, 171, 0.12)",
      }}
    >
      <form noValidate className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <Input
          label="事業所"
          placeholder="営業所を入力してください。"
          {...register("company_name")}
          error={errors.company_name?.message}
          disabled
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="名前"
            placeholder="名前を入力してください。"
            {...register("full_name")}
            error={errors.full_name?.message}
            maxLength={255}
          />

          <Controller
            control={control}
            name="role"
            render={({ field: { onChange, value } }) => {
              const selectedRole = value
                ? optionsForRole.find((i) => i.value === value)
                : undefined;

              return (
                <Select
                  label="役割"
                  value={selectedRole}
                  onChange={(item) => {
                    onChange(item?.value);
                  }}
                  options={optionsForRole}
                  isDisabled={isMyProfile && role !== RoleEnum.COMPANY_ADMIN}
                />
              );
            }}
          />
        </div>

        {currentUserRole != RoleEnum.OTHER && (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="役職"
              placeholder="役職を入力してください。"
              {...register("position")}
              error={errors.position?.message}
              disabled={role === RoleEnum.OTHER}
              maxLength={255}
            />

            <Input
              label="部署"
              placeholder="部署を入力してください。"
              {...register("department")}
              error={errors.department?.message}
              disabled={role === RoleEnum.OTHER}
              maxLength={255}
            />
          </div>
        )}

        {[
          RoleEnum.SUPER_ADMIN,
          RoleEnum.COMPANY_ADMIN,
          RoleEnum.OTHER,
        ].includes(currentUserRole) ? null : (
          <>
            {currentUserRole === RoleEnum.HEAD_OFFICE_USER && (
              <Controller
                control={control}
                name="group_ids"
                render={({ field: { onChange } }) => {
                  const selectedValue = groupsInCompany.filter((val) =>
                    (watch("group_ids") || []).includes(val.value)
                  );
                  return (
                    <CheckboxMultiSelect
                      label="グループ"
                      value={selectedValue as any}
                      options={groupsInCompany}
                      isDisabled={
                        !RoleManagerExcute.GROUP.MOVING[
                          role || RoleEnum.OTHER
                        ] ||
                        (isMyProfile && role !== RoleEnum.COMPANY_ADMIN)
                      }
                      onChange={(data) => {
                        const values = data
                          ? data.map((item) => item.value)
                          : [];
                        onChange(values);
                      }}
                    />
                  );
                }}
              />
            )}

            {currentUserRole !== RoleEnum.HEAD_OFFICE_USER && (
              <Controller
                control={control}
                name="store_id"
                render={({ field: { onChange } }) => {
                  const selectedValue = shopsInCompany.find(
                    (shop) => shop.value === watch("store_id")
                  );
                  return (
                    <Select
                      label="店舗"
                      value={selectedValue}
                      onChange={(item) => {
                        onChange(item?.value);
                      }}
                      placeholder="店舗"
                      required
                      options={shopsInCompany}
                      isSearchable
                      isDisabled={
                        isDisabledStoreSelect ||
                        !RoleManagerExcute.USER_IN_STORE.MOVING[
                          role || RoleEnum.OTHER
                        ] ||
                        (isMyProfile && role !== RoleEnum.COMPANY_ADMIN)
                      }
                      error={errors.store_id?.message}
                    />
                  );
                }}
              />
            )}
          </>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Controller
            control={control}
            name="phone"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <NumberString
                label="電話番号"
                name="phone"
                placeholder="電話番号を入力してください。"
                error={error?.message ?? ""}
                value={value || ""}
                onChange={onChange}
                maxLength={255}
                allowHyphen
              />
            )}
          />

          <Input
            label="メールアドレス"
            placeholder="メールアドレスを入力してください。"
            {...register("email")}
            error={errors.email?.message}
            disabled={isMyProfile && role !== RoleEnum.COMPANY_ADMIN}
            maxLength={255}
          />
        </div>

        <Input
          label="住所"
          placeholder="〒000-0000 東京都新宿区西新宿2-8-1"
          multiline
          maxLength={255}
          {...register("address")}
          error={errors.address?.message}
        />

        <div className="flex justify-end gap-2">
          {isMyProfile ? (
            <Button type="button" onClick={handleOpenPasswordModal}>
              パスワード変更
            </Button>
          ) : (
            <Button
              className="!rounded-lg"
              buttonType="text"
              disabled={isLoading || (!hasUpdatePermission && !isMyProfile)}
              onClick={handleResendInfor}
            >
              再招待
            </Button>
          )}
          <Button
            type="submit"
            buttonType="primary"
            className="!rounded-lg"
            disabled={
              isLoading ||
              (!hasUpdatePermission && !isMyProfile) ||
              !isEmpty(errors)
            }
          >
            情報更新
          </Button>
        </div>
      </form>

      <UpdatePasswordForm
        open={openPasswordModal}
        onClose={handleClosePasswordModal}
      />

      {!hasUpdatePermission && !isMyProfile && (
        <div
          className="absolute top-0 right-0 w-full h-full rounded-2xl bg-white/20 z-10 cursor-not-allowed"
          style={{
            boxShadow:
              "0 0 2px 0 rgba(145, 158, 171, 0.20), 0 12px 24px -4px rgba(145, 158, 171, 0.12)",
          }}
        />
      )}
    </div>
  );
};

export { UpdateInforForm };
